import React, { useState } from 'react';
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  Filter,
  TextField,
  TextInput,
  Button,
  useRecordContext,
  ReferenceManyField,
  ReferenceField,
  Pagination,
  useGetList,
} from 'react-admin';
import HNList from '../components/HNList';
import HNForm from '../components/HNForm';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Theme, useMediaQuery } from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';

const ProfileFilter = (props: any) => (
  <Filter {...props}>
    <TextInput style={{ minWidth: '300px' }} source="search" label="Search" alwaysOn />
  </Filter>
);

const ListeningsButton: React.FC<any> = ({ onClick }: { onClick: (record: any) => void }) => {
  const record = useRecordContext();
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  return isSmall ? (
    <IconButton color="primary" onClick={() => onClick(record)} title="View Listenings" size="small">
      <VisibilityIcon />
    </IconButton>
  ) : (
    <Button label="Listenings" onClick={() => onClick(record)} />
  );
};

export const ProfileList: React.FC<any> = (props) => {
  const [open, setOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);

  const handleOpenModal = (record: any) => {
    setSelectedProfile(record);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedProfile(null);
  };

  const monthsBetween = (start: string) => {
    const endDate = new Date();
    const startDate = new Date(start);
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months += endDate.getMonth() - startDate.getMonth();

    months += (endDate.getDate() - startDate.getDate()) / 30.44;
    return months > 0 ? months : 1;
  };

  const { data: listeningsData } = useGetList(
    'listening',
    {
      filter: { profile_id: selectedProfile?.id },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'count', order: 'DESC' },
    },
    { enabled: !!selectedProfile },
  );
  const totalListenings = listeningsData ? listeningsData.reduce((sum, l) => sum + (l.count || 0), 0) : 0;
  const months = selectedProfile?.created_at ? monthsBetween(selectedProfile.created_at) : 1;
  const avgPerMonth = months > 0 ? (totalListenings / months).toFixed(2) : totalListenings;

  return (
    <HNList {...props} filters={<ProfileFilter />} sort={{ field: 'created_at', order: 'DESC' }}>
      <Datagrid>
        <TextField
          source="anonymous_id"
          label="Anonymous ID"
          emptyText="-"
          sx={{
            display: 'block',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={(e: any) => {
            const text = e.currentTarget.textContent;
            if (text && text !== '-') {
              navigator.clipboard.writeText(text);
            }
          }}
        />
        <TextField source="first_name" label="First name" emptyText="-" />
        <TextField source="last_name" label="Last name" emptyText="-" />
        <TextField source="email" label="User email" emptyText="-" />
        <TextField source="created_at" label="Created At" emptyText="-" />
        <TextField source="timezone" label="Timezone" />
        <BooleanField source="is_notification_on" label="Is Notifications On ?" emptyText="No" />
        <ListeningsButton onClick={handleOpenModal} />
        <EditButton />
        <DeleteButton />
      </Datagrid>
      <Dialog open={open} onClose={handleCloseModal} maxWidth="xl" fullWidth>
        <DialogTitle>Listenings for {selectedProfile?.email || selectedProfile?.anonymous_id}</DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Total listenings: {totalListenings}</div>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Average per month: {avgPerMonth}</div>
              <ReferenceManyField
                reference="listening"
                target="profile_id"
                record={selectedProfile}
                pagination={<Pagination />}
                perPage={25}
                sort={{ field: 'count', order: 'DESC' }}
                label="Listenings">
                <Datagrid bulkActionButtons={false}>
                  <TextField source="count" label="Count" />
                  <ReferenceField source="track_id" reference="track" label="Track">
                    <TextField source="name" label="Track" />
                  </ReferenceField>
                  <ReferenceField source="track_id" reference="track" label="Title">
                    <ReferenceField source="title_id" reference="title">
                      <TextField source="name" label="Title" />
                    </ReferenceField>
                  </ReferenceField>
                </Datagrid>
              </ReferenceManyField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button label="Close" onClick={handleCloseModal} />
        </DialogActions>
      </Dialog>
    </HNList>
  );
};

const ProfileTitle: React.FC<any> = ({ record }) => <span>{record && `Profile "${record.email}"`}</span>;

export const ProfileEdit: React.FC<any> = (props) => (
  <Edit title={<ProfileTitle />} {...props}>
    <HNForm {...props}>
      <TextInput source="email" disabled />
      <TextInput source="first_name" />
      <TextInput source="last_name" />
      <TextInput source="anonymous_id" label="Anonymous ID" />
      <TextInput source="timezone" disabled />
      <BooleanInput source="is_notification_on" label="Is Notifications On ?" defaultValue={false} />
    </HNForm>
  </Edit>
);
